import {
  type BufferGeometry,
  CircleGeometry,
  type Color,
  CylinderGeometry,
  ExtrudeGeometry,
  LatheGeometry,
  PlaneGeometry,
  Shape,
  ShapeGeometry,
  TorusGeometry,
  Vector2,
} from 'three';
import type { BodyKind } from '../../config/eras';
import { type CarKit, drapeTop, facing, Hull, M, screenGeometry, type Side, SIDES, stripeShape, V } from './kit';
import type { DriverSpec } from './parts';

/**
 * The era bodies. Each builds its shell, trim and details into the kit
 * and says where its wheels and driver go. All caricatured the same way: toy
 * proportions, fat tyres, a big head.
 */

export interface AxleSpec {
  x: number;
  r: number;
  w: number;
  /** Wheel centre offset from the middle of the car. */
  z: number;
}

export interface BodyLayout {
  rear: AxleSpec;
  front: AxleSpec;
  driver: DriverSpec;
}

const PI = Math.PI;
const FWD = V(1, 0, 0);
const BACK = V(-1, 0, 0);

/** A flat rectangle in the side-view plane. */
const rect = (x: number, y: number, w: number, h: number): BufferGeometry => new PlaneGeometry(w, h).translate(x, y, 0);

/** A cockpit opening with a padded rim, bent over the body's top. */
function cockpit(k: CarKit, h: Hull, x: number, halfLen: number, halfW: number): void {
  const flat = M(x, 0, 0, -PI / 2, 0, 0, halfLen / 0.34, halfW / 0.34, 1);
  k.add(drapeTop(new CircleGeometry(0.34, 20).applyMatrix4(flat), h, 0, 0.005), k.c.dark);
  k.add(drapeTop(new TorusGeometry(0.34, 0.045, 5, 22).applyMatrix4(flat), h, 0, 0.012), k.c.brown);
}

/** Wishbones from the chassis out to each wheel. */
function wishbones(k: CarKit, x: number, inner: number, outer: number, y0: number, y1: number, r = 0.03): void {
  for (const s of SIDES) {
    k.rod(V(x - 0.12, y0, s * inner), V(x, y0 + 0.02, s * outer), r, k.c.metal);
    k.rod(V(x + 0.1, y1, s * inner), V(x, y1 - 0.02, s * outer), r, k.c.metal);
  }
}

/** A flat piece drawn side-on (x, y), t thick, standing at z. */
function sidePlate(k: CarKit, color: Color, pts: [number, number][], z: number, t = 0.02): void {
  const shape = new Shape(pts.map(([x, y]) => new Vector2(x, y)));
  k.add(new ExtrudeGeometry(shape, { depth: t, bevelEnabled: false }).translate(0, 0, z - t / 2), color);
}

/** A thin plate seen side-on as the line (x0, y0) → (x1, y1), `span` wide across the car. */
function slat(k: CarKit, color: Color, x0: number, y0: number, x1: number, y1: number, span: number, t = 0.025): void {
  const a = Math.atan2(y1 - y0, x1 - x0);
  k.box(color, (x0 + x1) / 2, (y0 + y1) / 2, 0, Math.hypot(x1 - x0, y1 - y0), t, span, 0, 0, x1 < x0 ? a - PI : a);
}

function mirrors(k: CarKit, x: number, y: number, z: number, from: number): void {
  for (const s of SIDES) {
    k.rod(V(x - 0.02, y - 0.12, s * from), V(x, y - 0.02, s * z), 0.016, k.c.metal, 4);
    k.box(k.c.main, x, y, s * (z + 0.02), 0.05, 0.07, 0.13);
  }
}

// ---------------------------------------------------------------------------

/** 1932: tall and narrow, a long bonnet behind a pointed radiator, a beetle-back tail, side exhaust. */
function monoposto(k: CarKit): BodyLayout {
  const c = k.c;
  const body = k.hull(
    new Hull(
      [
        { x: -1.86, w: 0.02, top: 0.74, bot: 0.68 },
        { x: -1.62, w: 0.19, top: 0.88, bot: 0.47 },
        { x: -1.2, w: 0.3, top: 0.96, bot: 0.37 },
        { x: -0.75, w: 0.34, top: 0.99, bot: 0.35 },
        { x: -0.25, w: 0.36, top: 0.95, bot: 0.33 },
        { x: 0.05, w: 0.36, top: 0.99, bot: 0.33 },
        { x: 0.6, w: 0.34, top: 0.96, bot: 0.35 },
        { x: 1.25, w: 0.32, top: 0.93, bot: 0.37 },
        { x: 1.56, w: 0.3, top: 0.91, bot: 0.39 },
      ],
      3.2,
    ),
    c.main,
  );
  cockpit(k, body, -0.5, 0.36, 0.26);
  k.add(screenGeometry(0.4, 0.15, 0.04, 0.45), c.glass, M(-0.1, body.topY(-0.1, 0) - 0.02, 0));

  // The radiator: a pointed shell around a slatted grille.
  const rad = new Shape();
  rad.moveTo(0, -0.25);
  rad.quadraticCurveTo(0.2, -0.1, 0.2, 0.08);
  rad.quadraticCurveTo(0.2, 0.24, 0, 0.24);
  rad.quadraticCurveTo(-0.2, 0.24, -0.2, 0.08);
  rad.quadraticCurveTo(-0.2, -0.1, 0, -0.25);
  k.add(new ShapeGeometry(rad, 8), c.bright, facing(V(1.564, 0.655, 0), FWD, V(0, 1, 0), 1.15, 1.08));
  k.add(new ShapeGeometry(rad, 8), c.dark, facing(V(1.572, 0.66, 0), FWD));
  for (let i = -2; i <= 2; i++) k.box(c.bright, 1.578, 0.64, i * 0.055, 0.012, 0.34, 0.016);
  k.add(new CylinderGeometry(0.05, 0.06, 0.07, 8), c.bright, M(1.47, body.topY(1.47, 0) + 0.02, 0));

  // Leather bonnet straps and louvres.
  for (const x of [0.62, 1.14]) k.add(drapeTop(new PlaneGeometry(0.07, 0.6).applyMatrix4(M(x, 0, 0, -PI / 2, 0, 0)), body, 0, 0.006), c.brown);
  for (let i = 0; i < 5; i++) k.sideDecal(body, rect(0.42 + i * 0.1, 0.66, 0.035, 0.2), c.dark);

  // Straight-eight exhaust down the left side.
  const ez = -0.45;
  k.rod(V(1.02, 0.5, ez), V(-1.6, 0.5, ez), 0.062, c.metal, 8);
  k.add(new CylinderGeometry(0.1, 0.062, 0.34, 8), c.metal, M(-1.77, 0.5, ez, 0, 0, PI / 2));
  k.add(new CircleGeometry(0.085, 10), c.dark, facing(V(-1.943, 0.5, ez), BACK));
  for (let i = 0; i < 4; i++) {
    const x0 = 0.4 + i * 0.2;
    k.rod(V(x0, 0.7, body.sideZ(x0, 0.7, -1) + 0.02), V(x0 - 0.16, 0.5, ez), 0.042, c.metal);
  }

  // Beam axles, a filler cap and the numbers.
  k.rod(V(1.3, 0.44, -0.62), V(1.3, 0.44, 0.62), 0.04, c.metal);
  k.rod(V(-1.05, 0.47, -0.64), V(-1.05, 0.47, 0.64), 0.045, c.metal);
  k.add(new CylinderGeometry(0.07, 0.08, 0.06, 10), c.bright, M(-1.45, body.topY(-1.45, 0) + 0.01, 0, 0, 0, 0.2));
  k.sideRoundel(body, 0.08, 0.66, 0.2);
  k.topRoundel(body, -1.28, 0, 0.15);

  return {
    rear: { x: -1.05, r: 0.47, w: 0.24, z: 0.74 },
    front: { x: 1.3, r: 0.44, w: 0.2, z: 0.7 },
    driver: { x: -0.5, y: 1.12, lean: 0.08, wx: -0.14, wy: 1.13, wr: 0.2, rake: 0.25 },
  };
}

function flames(): { outer: Shape; inner: Shape } {
  const outer = new Shape();
  outer.moveTo(1.74, 0.73);
  outer.bezierCurveTo(1.2, 0.86, 0.3, 0.9, -0.5, 0.82);
  outer.quadraticCurveTo(0.1, 0.8, 0.46, 0.72);
  outer.quadraticCurveTo(0.05, 0.74, -0.25, 0.68);
  outer.quadraticCurveTo(0.3, 0.64, 0.6, 0.61);
  outer.quadraticCurveTo(-0.05, 0.6, -0.75, 0.54);
  outer.quadraticCurveTo(0.05, 0.5, 0.44, 0.49);
  outer.quadraticCurveTo(0.2, 0.47, -0.08, 0.42);
  outer.bezierCurveTo(0.6, 0.39, 1.3, 0.42, 1.74, 0.5);
  outer.quadraticCurveTo(1.82, 0.62, 1.74, 0.73);
  // The inner flame: the same licks, shrunk toward the nose.
  const inner = new Shape(outer.getPoints(10).map((p) => new Vector2(1.8 + (p.x - 1.8) * 0.72, 0.62 + (p.y - 0.62) * 0.62)));
  return { outer, inner };
}

/** 1937: the silver arrow. A long smooth bullet, oval mouth, headrest fin and hot-rod flames. */
function streamliner(k: CarKit): BodyLayout {
  const c = k.c;
  const body = k.hull(
    new Hull(
      [
        { x: -2.05, w: 0.02, top: 0.63, bot: 0.57 },
        { x: -1.75, w: 0.2, top: 0.8, bot: 0.4 },
        { x: -1.3, w: 0.33, top: 0.93, bot: 0.31 },
        { x: -0.8, w: 0.4, top: 0.98, bot: 0.27 },
        { x: -0.3, w: 0.42, top: 0.98, bot: 0.26 },
        { x: 0.3, w: 0.41, top: 0.96, bot: 0.27 },
        { x: 0.9, w: 0.37, top: 0.9, bot: 0.29 },
        { x: 1.4, w: 0.31, top: 0.82, bot: 0.33 },
        { x: 1.75, w: 0.24, top: 0.74, bot: 0.38 },
        { x: 1.92, w: 0.17, top: 0.68, bot: 0.43 },
      ],
      2.3,
      14,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: -1.6, w: 0.02, top: 0.88, bot: 0.84 },
        { x: -1.15, w: 0.12, top: 1.08, bot: 0.8 },
        { x: -0.84, w: 0.16, top: 1.15, bot: 0.8 },
        { x: -0.74, w: 0.14, top: 1.1, bot: 0.8 },
      ],
      2.2,
      10,
    ),
    c.main,
  );
  cockpit(k, body, -0.33, 0.36, 0.27);
  k.add(screenGeometry(0.46, 0.15, 0.08, 0.55), c.glass, M(0.02, body.topY(0.02, 0) - 0.02, 0));

  // Oval mouth in a bright ring.
  k.add(new CircleGeometry(0.15, 18), c.dark, facing(V(1.924, 0.555, 0), FWD, V(0, 1, 0), 0.95, 0.72));
  k.add(new TorusGeometry(0.15, 0.022, 4, 18), c.bright, facing(V(1.92, 0.555, 0), FWD, V(0, 1, 0), 0.95, 0.72));
  for (const z of [-0.06, 0, 0.06]) k.box(c.bright, 1.928, 0.555, z, 0.01, 0.17, 0.014);

  const f = flames();
  k.sideDecal(body, new ShapeGeometry(f.outer, 8), c.accent, SIDES, 0.008);
  k.sideDecal(body, new ShapeGeometry(f.inner, 8), c.ochre, SIDES, 0.013);
  k.sideRoundel(body, -0.12, 0.62, 0.21, SIDES, 0.02);
  k.topRoundel(body, -1.42, 0, 0.14);

  // Exhaust stubs gathering into a low pipe on the right.
  const ez = 0.44;
  k.rod(V(1.0, 0.4, ez), V(-1.45, 0.4, ez), 0.055, c.metal, 8);
  k.add(new CircleGeometry(0.045, 8), c.dark, facing(V(-1.452, 0.4, ez), BACK));
  for (let i = 0; i < 4; i++) {
    const x0 = 0.45 + i * 0.18;
    k.rod(V(x0, 0.5, body.sideZ(x0, 0.5, 1) - 0.02), V(x0 - 0.16, 0.4, ez), 0.035, c.metal);
  }
  wishbones(k, 1.28, 0.22, 0.6, 0.38, 0.5);
  wishbones(k, -1.05, 0.28, 0.61, 0.42, 0.54);

  return {
    rear: { x: -1.05, r: 0.47, w: 0.3, z: 0.76 },
    front: { x: 1.28, r: 0.42, w: 0.24, z: 0.72 },
    driver: { x: -0.4, y: 1.06, lean: 0.12, wx: -0.05, wy: 1.08, wr: 0.19, rake: 0.3 },
  };
}

/** 1954: the front-engined cigar. Cream nose with an egg-crate grille, bonnet bulge, megaphone exhaust. */
function frontEngine(k: CarKit): BodyLayout {
  const c = k.c;
  const body = k.hull(
    new Hull([
      { x: -1.95, w: 0.02, top: 0.66, bot: 0.6 },
      { x: -1.65, w: 0.2, top: 0.84, bot: 0.44 },
      { x: -1.2, w: 0.33, top: 0.95, bot: 0.34 },
      { x: -0.6, w: 0.41, top: 0.99, bot: 0.29 },
      { x: 0.0, w: 0.41, top: 0.98, bot: 0.29 },
      { x: 0.6, w: 0.38, top: 0.93, bot: 0.3 },
      { x: 1.2, w: 0.34, top: 0.86, bot: 0.32 },
      { x: 1.55, w: 0.29, top: 0.79, bot: 0.35 },
    ]),
    c.main,
  );
  k.hull(
    new Hull([
      { x: 1.55, w: 0.29, top: 0.79, bot: 0.35 },
      { x: 1.87, w: 0.235, top: 0.72, bot: 0.39 },
    ]),
    c.accent,
  );
  // Bonnet bulge and headrest.
  k.hull(
    new Hull(
      [
        { x: 0.0, w: 0.13, top: 1.03, bot: 0.9 },
        { x: 0.7, w: 0.13, top: 1.0, bot: 0.86 },
        { x: 1.2, w: 0.1, top: 0.91, bot: 0.8 },
        { x: 1.32, w: 0.02, top: 0.87, bot: 0.83 },
      ],
      2.4,
      10,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: -1.55, w: 0.02, top: 0.9, bot: 0.86 },
        { x: -1.2, w: 0.13, top: 1.1, bot: 0.84 },
        { x: -0.95, w: 0.15, top: 1.14, bot: 0.84 },
        { x: -0.88, w: 0.13, top: 1.09, bot: 0.84 },
      ],
      2.2,
      10,
    ),
    c.main,
  );
  cockpit(k, body, -0.5, 0.36, 0.27);
  k.add(screenGeometry(0.52, 0.14, 0.1, 0.6), c.glass, M(-0.12, body.topY(-0.12, 0) - 0.02, 0));

  // Oval egg-crate grille.
  k.add(new CircleGeometry(0.2, 20), c.dark, facing(V(1.874, 0.555, 0), FWD, V(0, 1, 0), 0.95, 0.72));
  for (const z of [-0.09, 0, 0.09]) k.box(c.bright, 1.878, 0.555, z, 0.012, 0.25, 0.014);
  for (const y of [-0.045, 0.045]) k.box(c.bright, 1.879, 0.555 + y, 0, 0.012, 0.014, 0.35);
  for (let i = 0; i < 6; i++) k.sideDecal(body, rect(0.42 + i * 0.1, 0.63, 0.035, 0.17), c.dark);

  // Megaphone exhaust along the right side.
  const ez = 0.5;
  const ey = 0.46;
  k.rod(V(0.78, ey, ez), V(-1.5, ey, ez), 0.055, c.metal, 8);
  k.add(new CylinderGeometry(0.12, 0.055, 0.46, 10), c.metal, M(-1.73, ey, ez, 0, 0, PI / 2));
  k.add(new CircleGeometry(0.105, 10), c.dark, facing(V(-1.962, ey, ez), BACK));
  for (const x0 of [0.72, 0.97, 1.22]) k.rod(V(x0, 0.62, body.sideZ(x0, 0.62, 1) - 0.02), V(x0 - 0.2, ey, ez), 0.04, c.metal);

  k.add(new CylinderGeometry(0.075, 0.085, 0.06, 10), c.bright, M(-1.5, body.topY(-1.5, 0) + 0.01, 0, 0, 0, 0.3));
  k.sideRoundel(body, 0.22, 0.64, 0.19);
  k.topRoundel(body, -1.3, 0, 0.15);
  wishbones(k, 1.25, 0.26, 0.62, 0.36, 0.5);
  k.rod(V(-1.1, 0.46, -0.64), V(-1.1, 0.46, 0.64), 0.04, c.metal);

  return {
    rear: { x: -1.1, r: 0.46, w: 0.3, z: 0.78 },
    front: { x: 1.25, r: 0.41, w: 0.24, z: 0.74 },
    driver: { x: -0.55, y: 1.07, lean: 0.1, wx: -0.2, wy: 1.1, wr: 0.19, rake: 0.3 },
  };
}

/** 1961: the little rear-engined red racer from the car sheet (the Phase 1 hero, unchanged). */
function rearEngine(k: CarKit): BodyLayout {
  const c = k.c;
  const prof: [number, number][] = [
    [0.001, -1.63],
    [0.33, -1.61],
    [0.44, -1.5],
    [0.5, -1.2],
    [0.53, -0.7],
    [0.53, -0.1],
    [0.5, 0.45],
    [0.45, 0.95],
    [0.4, 1.3],
    [0.33, 1.52],
    [0.22, 1.63],
    [0.001, 1.66],
  ];
  const lathe = new LatheGeometry(
    prof.map(([r, x]) => new Vector2(r, x)),
    11,
  );
  lathe.rotateZ(-PI / 2);
  lathe.scale(1, 0.8, 1.14);
  lathe.translate(0, 0.54, 0);
  k.add(lathe, c.main);
  k.add(new CircleGeometry(0.34, 12), c.dark, M(-0.36, 0.955, 0, -PI / 2, 0, 0, 1.25, 0.9, 1));
  k.add(new TorusGeometry(0.34, 0.05, 5, 14), c.brown, M(-0.36, 0.95, 0, -PI / 2, 0, 0, 1.25, 0.9, 1));
  k.add(screenGeometry(0.42, 0.17, 0.06, 0.4), c.glass, M(0.08, 0.93, 0));
  k.add(new CylinderGeometry(0.08, 0.09, 0.07, 10), c.metal, M(0.95, 0.9, 0, 0, 0, -0.3));
  k.add(new TorusGeometry(0.15, 0.04, 5, 12, PI), c.metal, M(-0.74, 0.93, 0, 0, PI / 2, 0));
  k.add(new CylinderGeometry(0.11, 0.12, 0.08, 10), c.metal, M(-1.15, 0.935, 0, 0, 0, 0.3));
  k.add(new CylinderGeometry(0.16, 0.16, 0.46, 8), c.metal, M(-1.08, 0.46, 0, PI / 2, 0, 0));

  k.flatRoundel(M(1.625, 0.585, 0, -0.35, PI / 2, 0, 1, 1, 1, 'YXZ'), 0.19);
  k.flatRoundel(M(0.42, 0.6, -0.575, 0, PI, 0, 1, 1, 1, 'YXZ'), 0.26);
  k.flatRoundel(M(0.42, 0.6, 0.575, 0, 0, 0, 1, 1, 1, 'YXZ'), 0.26);
  k.flatRoundel(M(-1.64, 0.56, 0, 0, -PI / 2, 0, 1, 1, 1, 'YXZ'), 0.21);

  for (const z of [-0.24, 0.24]) {
    k.add(new CylinderGeometry(0.075, 0.085, 0.5, 10), c.metal, M(-1.72, 0.3, z, 0, 0, PI / 2 - 0.06));
    k.add(new CircleGeometry(0.06, 10), c.dark, M(-1.975, 0.315, z, 0, -PI / 2, 0));
  }
  const REAR_X = -1.08;
  const FRONT_X = 1.2;
  for (const s of SIDES) {
    k.rod(V(REAR_X + 0.15, 0.36, s * 0.3), V(REAR_X, 0.44, s * 0.72), 0.035, c.metal);
    k.rod(V(REAR_X - 0.2, 0.56, s * 0.32), V(REAR_X, 0.5, s * 0.72), 0.035, c.metal);
    k.rod(V(FRONT_X - 0.15, 0.3, s * 0.26), V(FRONT_X, 0.32, s * 0.6), 0.035, c.metal);
    k.rod(V(FRONT_X + 0.12, 0.46, s * 0.24), V(FRONT_X, 0.36, s * 0.6), 0.035, c.metal);
  }
  return {
    rear: { x: REAR_X, r: 0.47, w: 0.46, z: 0.9 },
    front: { x: FRONT_X, r: 0.34, w: 0.3, z: 0.78 },
    driver: { x: -0.42, y: 1.08, lean: 0, wx: -0.08, wy: 1.1, wr: 0.16, rake: 0.35 },
  };
}

/** 1972: the wedge. Flat nose with winglets, side radiators, a towering airbox, bare engine, big rear wing. */
function wedge(k: CarKit): BodyLayout {
  const c = k.c;
  const tub = k.hull(
    new Hull(
      [
        { x: -0.98, w: 0.3, top: 0.72, bot: 0.2 },
        { x: -0.7, w: 0.36, top: 0.82, bot: 0.16 },
        { x: -0.1, w: 0.38, top: 0.84, bot: 0.16 },
        { x: 0.3, w: 0.38, top: 0.78, bot: 0.16 },
        { x: 0.9, w: 0.36, top: 0.62, bot: 0.17 },
        { x: 1.5, w: 0.3, top: 0.45, bot: 0.18 },
        { x: 2.05, w: 0.22, top: 0.3, bot: 0.2 },
      ],
      5,
    ),
    c.main,
  );
  cockpit(k, tub, -0.38, 0.34, 0.25);
  k.add(screenGeometry(0.42, 0.1, 0.08, 0.75), c.glass, M(0.04, tub.topY(0.04, 0) - 0.02, 0));
  k.sideDecal(tub, new ShapeGeometry(stripeShape(tub, -0.9, 1.9, 0.07, 0.03), 1), c.accent);

  const pod = new Hull(
    [
      { x: -0.55, w: 0.16, top: 0.58, bot: 0.18, zc: 0.5 },
      { x: -0.2, w: 0.2, top: 0.62, bot: 0.16, zc: 0.52 },
      { x: 0.2, w: 0.18, top: 0.52, bot: 0.16, zc: 0.52 },
      { x: 0.42, w: 0.12, top: 0.42, bot: 0.18, zc: 0.52 },
    ],
    6,
  );
  for (const [p, s] of [
    [pod, 1],
    [pod.mirrored(), -1],
  ] as [Hull, Side][]) {
    k.hull(p, c.main);
    k.add(new PlaneGeometry(0.2, 0.17), c.dark, facing(V(0.425, 0.3, s * 0.52), FWD));
    k.sideDecal(p, new ShapeGeometry(stripeShape(p, -0.52, 0.4, 0.05, 0.03), 1), c.accent, [s]);
    k.sideRoundel(p, -0.08, 0.38, 0.15, [s]);
  }

  // Nose winglets.
  for (const s of SIDES) {
    k.box(c.main, 1.9, 0.27, s * 0.42, 0.32, 0.035, 0.44, 0, 0, -0.1);
    k.box(c.accent, 1.9, 0.31, s * 0.645, 0.34, 0.16, 0.025);
  }
  k.topRoundel(tub, 1.28, 0, 0.16);

  // The airbox, towering over the engine.
  const air = k.hull(
    new Hull(
      [
        { x: -1.26, w: 0.1, top: 1.0, bot: 0.6 },
        { x: -1.02, w: 0.16, top: 1.28, bot: 0.6 },
        { x: -0.88, w: 0.17, top: 1.34, bot: 0.7 },
      ],
      4,
    ),
    c.main,
  );
  k.add(new CircleGeometry(1, 16), c.dark, facing(V(-0.875, 1.18, 0), FWD, V(0, 1, 0), 0.12, 0.11));
  k.sideRoundel(air, -1.06, 1.0, 0.13);

  // Bare V8, gearbox, exhausts.
  k.box(c.metal, -1.26, 0.43, 0, 0.56, 0.36, 0.46);
  for (const s of SIDES) k.box(c.brown, -1.25, 0.64, s * 0.13, 0.5, 0.06, 0.1);
  k.box(c.metal, -1.72, 0.4, 0, 0.44, 0.28, 0.34);
  for (const s of SIDES) for (const dy of [0, 0.09]) k.rod(V(-1.15, 0.36 + dy, s * 0.24), V(-1.98, 0.42 + dy, s * (0.2 + dy)), 0.04, c.metal);

  // Rear wing on a single pylon.
  k.box(c.main, -1.92, 1.2, 0, 0.42, 0.04, 1.5, 0, 0, -0.12);
  k.box(c.main, -2.12, 1.3, 0, 0.2, 0.03, 1.5, 0, 0, -0.45);
  for (const s of SIDES) k.box(c.accent, -1.98, 1.2, s * 0.765, 0.62, 0.42, 0.025);
  k.box(c.metal, -1.82, 0.86, 0, 0.26, 0.62, 0.035);

  mirrors(k, 0.12, 0.94, 0.46, 0.34);
  wishbones(k, 1.32, 0.24, 0.64, 0.28, 0.42, 0.025);
  wishbones(k, -1.08, 0.18, 0.63, 0.3, 0.54, 0.025);

  return {
    rear: { x: -1.08, r: 0.49, w: 0.56, z: 0.9 },
    front: { x: 1.32, r: 0.36, w: 0.3, z: 0.78 },
    driver: { x: -0.36, y: 0.72, lean: 0.5, wx: 0.0, wy: 0.84, wr: 0.14, rake: 0.6 },
  };
}

/** 1985: the turbo car. Low and flat, long coke-bottle pods with a chevron, wide wings, turbo plumbing. */
function turbo(k: CarKit): BodyLayout {
  const c = k.c;
  const tub = k.hull(
    new Hull(
      [
        { x: -1.72, w: 0.2, top: 0.5, bot: 0.2 },
        { x: -1.3, w: 0.28, top: 0.66, bot: 0.16 },
        { x: -0.7, w: 0.34, top: 0.84, bot: 0.14 },
        { x: -0.2, w: 0.37, top: 0.8, bot: 0.14 },
        { x: 0.3, w: 0.36, top: 0.74, bot: 0.14 },
        { x: 1.0, w: 0.3, top: 0.6, bot: 0.16 },
        { x: 1.6, w: 0.18, top: 0.46, bot: 0.2 },
      ],
      3.4,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: 1.6, w: 0.18, top: 0.46, bot: 0.2 },
        { x: 2.1, w: 0.06, top: 0.35, bot: 0.26 },
      ],
      3.4,
    ),
    c.accent,
  );
  cockpit(k, tub, -0.3, 0.33, 0.24);
  k.add(screenGeometry(0.36, 0.08, 0.06, 0.85), c.glass, M(0.06, tub.topY(0.06, 0) - 0.02, 0));
  k.topRoundel(tub, 1.22, 0, 0.15);
  k.sideRoundel(tub, -1.08, 0.58, 0.12);

  const pod = new Hull(
    [
      { x: -1.42, w: 0.06, top: 0.36, bot: 0.16, zc: 0.34 },
      { x: -0.95, w: 0.14, top: 0.46, bot: 0.13, zc: 0.36 },
      { x: -0.5, w: 0.2, top: 0.52, bot: 0.12, zc: 0.48 },
      { x: 0.2, w: 0.2, top: 0.52, bot: 0.12, zc: 0.5 },
      { x: 0.42, w: 0.13, top: 0.42, bot: 0.14, zc: 0.52 },
    ],
    5,
  );
  const chevron = new Shape([new Vector2(0.4, 0.33), new Vector2(-0.42, 0.48), new Vector2(-0.24, 0.33), new Vector2(-0.42, 0.18)]);
  for (const [p, s] of [
    [pod, 1],
    [pod.mirrored(), -1],
  ] as [Hull, Side][]) {
    k.hull(p, c.main);
    k.add(new PlaneGeometry(0.22, 0.2), c.dark, facing(V(0.425, 0.28, s * 0.52), FWD));
    k.sideDecal(p, new ShapeGeometry(chevron), c.accent, [s]);
  }

  // Front wing, low and wide, and the rear wing with big endplates.
  k.box(c.accent, 1.9, 0.13, 0, 0.36, 0.035, 1.72, 0, 0, -0.06);
  k.box(c.accent, 1.77, 0.19, 0, 0.14, 0.025, 1.72, 0, 0, -0.4);
  for (const s of SIDES) k.box(c.main, 1.87, 0.19, s * 0.87, 0.44, 0.17, 0.025);
  k.box(c.main, -1.95, 0.98, 0, 0.5, 0.045, 1.4, 0, 0, -0.1);
  k.box(c.main, -2.18, 1.08, 0, 0.22, 0.035, 1.4, 0, 0, -0.5);
  for (const s of SIDES) k.box(c.accent, -2.02, 0.92, s * 0.715, 0.72, 0.56, 0.03);
  k.box(c.metal, -1.85, 0.7, 0, 0.3, 0.4, 0.035);

  // Turbos, wastegate pipe, exhausts, gearbox, roll hoop.
  for (const s of SIDES) {
    k.add(new TorusGeometry(0.09, 0.05, 6, 12), c.metal, M(-1.5, 0.52, s * 0.27));
    k.rod(V(-1.5, 0.52, s * 0.3), V(-2.0, 0.4, s * 0.14), 0.045, c.metal);
  }
  k.rod(V(-1.55, 0.62, 0), V(-2.05, 0.68, 0), 0.05, c.metal);
  k.add(new CircleGeometry(0.04, 8), c.dark, facing(V(-2.052, 0.68, 0), BACK));
  k.box(c.metal, -1.85, 0.36, 0, 0.4, 0.26, 0.32);
  k.add(new TorusGeometry(0.12, 0.03, 5, 10, PI), c.metal, M(-0.92, tub.topY(-0.92, 0) - 0.02, 0, 0, PI / 2, 0));

  mirrors(k, 0.15, 0.72, 0.5, 0.5);
  wishbones(k, 1.35, 0.22, 0.64, 0.26, 0.4, 0.025);
  wishbones(k, -1.1, 0.2, 0.61, 0.28, 0.52, 0.025);

  return {
    rear: { x: -1.1, r: 0.47, w: 0.56, z: 0.88 },
    front: { x: 1.35, r: 0.35, w: 0.34, z: 0.8 },
    driver: { x: -0.34, y: 0.7, lean: 0.55, wx: 0.02, wy: 0.8, wr: 0.13, rake: 0.6 },
  };
}

/** 1994: the high nose. A raised nose over a hung front wing, undercut pods, roll-hoop airbox, tall rear wing. */
function highNose(k: CarKit): BodyLayout {
  const c = k.c;
  const tub = k.hull(
    new Hull(
      [
        { x: -1.78, w: 0.12, top: 0.5, bot: 0.22 },
        { x: -1.5, w: 0.18, top: 0.64, bot: 0.18 },
        { x: -1.1, w: 0.27, top: 0.84, bot: 0.16 },
        { x: -0.75, w: 0.33, top: 0.9, bot: 0.15 },
        { x: -0.3, w: 0.35, top: 0.86, bot: 0.15 },
        { x: 0.2, w: 0.33, top: 0.84, bot: 0.18 },
        { x: 0.7, w: 0.26, top: 0.78, bot: 0.34 },
        { x: 1.2, w: 0.2, top: 0.71, bot: 0.4 },
        { x: 1.65, w: 0.14, top: 0.62, bot: 0.41 },
      ],
      3.2,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: 1.65, w: 0.14, top: 0.62, bot: 0.41 },
        { x: 2.05, w: 0.06, top: 0.52, bot: 0.44 },
      ],
      3.2,
    ),
    c.accent,
  );
  cockpit(k, tub, -0.28, 0.33, 0.23);
  k.add(screenGeometry(0.34, 0.07, 0.05, 0.9), c.glass, M(0.08, tub.topY(0.08, 0) - 0.02, 0));
  k.topRoundel(tub, 1.25, 0, 0.13);

  const air = k.hull(
    new Hull(
      [
        { x: -1.32, w: 0.08, top: 0.9, bot: 0.7 },
        { x: -1.0, w: 0.14, top: 1.14, bot: 0.78 },
        { x: -0.84, w: 0.16, top: 1.2, bot: 0.84 },
      ],
      3,
    ),
    c.main,
  );
  k.add(new CircleGeometry(1, 16), c.dark, facing(V(-0.835, 1.04, 0), FWD, V(0, 1, 0), 0.1, 0.1));
  k.sideRoundel(air, -1.06, 0.97, 0.1);

  const pod = new Hull(
    [
      { x: -1.3, w: 0.06, top: 0.4, bot: 0.2, zc: 0.3 },
      { x: -0.95, w: 0.13, top: 0.52, bot: 0.16, zc: 0.4 },
      { x: -0.5, w: 0.2, top: 0.6, bot: 0.16, zc: 0.48 },
      { x: 0.1, w: 0.2, top: 0.62, bot: 0.22, zc: 0.5 },
      { x: 0.3, w: 0.13, top: 0.58, bot: 0.3, zc: 0.5 },
    ],
    4,
  );
  for (const [p, s] of [
    [pod, 1],
    [pod.mirrored(), -1],
  ] as [Hull, Side][]) {
    k.hull(p, c.main);
    k.add(new CircleGeometry(1, 14), c.dark, facing(V(0.305, 0.45, s * 0.5), FWD, V(0, 1, 0), 0.1, 0.09));
    k.sideDecal(p, new ShapeGeometry(stripeShape(p, -0.85, 0.26, 0.06, 0.04), 1), c.accent, [s]);
    k.sideRoundel(p, -0.28, 0.4, 0.14, [s]);
  }

  // Front wing hung under the nose on two pylons.
  k.box(c.main, 1.9, 0.13, 0, 0.36, 0.035, 1.66, 0, 0, -0.06);
  k.box(c.accent, 1.77, 0.19, 0, 0.14, 0.025, 1.66, 0, 0, -0.4);
  for (const s of SIDES) {
    k.box(c.accent, 1.87, 0.2, s * 0.84, 0.44, 0.2, 0.025);
    k.box(c.main, 1.86, 0.28, s * 0.07, 0.22, 0.28, 0.02);
  }

  // Tall rear wing, beam wing, rain light, exhausts, gearbox.
  k.box(c.main, -1.94, 1.02, 0, 0.38, 0.04, 1.2, 0, 0, -0.14);
  k.box(c.main, -2.1, 1.13, 0, 0.18, 0.03, 1.2, 0, 0, -0.5);
  k.box(c.main, -1.86, 0.62, 0, 0.26, 0.03, 0.9, 0, 0, -0.1);
  for (const s of SIDES) k.box(c.accent, -1.98, 0.9, s * 0.615, 0.56, 0.64, 0.03);
  k.box(c.light, -2.03, 0.42, 0, 0.05, 0.07, 0.12);
  for (const s of SIDES) {
    k.rod(V(-1.7, 0.5, s * 0.12), V(-2.0, 0.53, s * 0.12), 0.04, c.metal);
    k.add(new CircleGeometry(0.032, 8), c.dark, facing(V(-2.002, 0.53, s * 0.12), BACK));
  }
  k.box(c.metal, -1.84, 0.36, 0, 0.36, 0.26, 0.3);

  mirrors(k, 0.1, 0.96, 0.46, 0.34);
  wishbones(k, 1.36, 0.14, 0.64, 0.3, 0.44, 0.025);
  wishbones(k, -1.1, 0.18, 0.61, 0.28, 0.5, 0.025);

  return {
    rear: { x: -1.1, r: 0.46, w: 0.5, z: 0.86 },
    front: { x: 1.36, r: 0.35, w: 0.32, z: 0.8 },
    driver: { x: -0.3, y: 0.7, lean: 0.55, wx: 0.04, wy: 0.8, wr: 0.13, rake: 0.6 },
  };
}

/** 2004: the red V10 car at its peak. Raised nose over a spoon front wing, bargeboards, chimneys and periscope exhausts, a tall narrow rear wing, a slim tail. */
function v10(k: CarKit): BodyLayout {
  const c = k.c;
  const tub = k.hull(
    new Hull(
      [
        { x: -1.8, w: 0.1, top: 0.46, bot: 0.24 },
        { x: -1.5, w: 0.16, top: 0.62, bot: 0.2 },
        { x: -1.1, w: 0.25, top: 0.82, bot: 0.16 },
        { x: -0.75, w: 0.32, top: 0.88, bot: 0.15 },
        { x: -0.3, w: 0.34, top: 0.84, bot: 0.15 },
        { x: 0.2, w: 0.31, top: 0.8, bot: 0.2 },
        { x: 0.7, w: 0.22, top: 0.72, bot: 0.36 },
        { x: 1.2, w: 0.16, top: 0.64, bot: 0.4 },
        { x: 1.75, w: 0.1, top: 0.55, bot: 0.41 },
      ],
      3.2,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: 1.75, w: 0.1, top: 0.55, bot: 0.41 },
        { x: 2.06, w: 0.05, top: 0.49, bot: 0.44 },
      ],
      3.2,
    ),
    c.accent,
  );
  cockpit(k, tub, -0.28, 0.33, 0.23);
  k.add(screenGeometry(0.34, 0.07, 0.05, 0.9), c.glass, M(0.08, tub.topY(0.08, 0) - 0.02, 0));
  k.topRoundel(tub, 1.22, 0, 0.12);

  // Roll-hoop airbox with the T-camera on top, kept under the helmet line.
  const air = k.hull(
    new Hull(
      [
        { x: -1.3, w: 0.08, top: 0.86, bot: 0.68 },
        { x: -1.0, w: 0.13, top: 1.1, bot: 0.76 },
        { x: -0.84, w: 0.15, top: 1.16, bot: 0.82 },
      ],
      3,
    ),
    c.main,
  );
  k.add(new CircleGeometry(1, 16), c.dark, facing(V(-0.835, 1.0, 0), FWD, V(0, 1, 0), 0.09, 0.1));
  k.box(c.accent, -0.95, 1.15, 0, 0.04, 0.07, 0.03);
  k.box(c.accent, -0.95, 1.19, 0, 0.06, 0.03, 0.24);
  k.sideRoundel(air, -1.07, 0.93, 0.09);

  // Tight coke-bottle pods with a cream stripe, chimneys and periscope exhausts.
  const pod = new Hull(
    [
      { x: -1.25, w: 0.05, top: 0.36, bot: 0.2, zc: 0.28 },
      { x: -0.95, w: 0.11, top: 0.46, bot: 0.16, zc: 0.36 },
      { x: -0.45, w: 0.19, top: 0.58, bot: 0.15, zc: 0.47 },
      { x: 0.1, w: 0.19, top: 0.6, bot: 0.2, zc: 0.49 },
      { x: 0.32, w: 0.12, top: 0.56, bot: 0.3, zc: 0.49 },
    ],
    4,
  );
  for (const [p, s] of [
    [pod, 1],
    [pod.mirrored(), -1],
  ] as [Hull, Side][]) {
    k.hull(p, c.main);
    k.add(new CircleGeometry(1, 14), c.dark, facing(V(0.325, 0.43, s * 0.49), FWD, V(0, 1, 0), 0.1, 0.1));
    k.sideDecal(p, new ShapeGeometry(stripeShape(p, -0.85, 0.3, 0.05, 0.05), 1), c.accent, [s]);
    k.sideRoundel(p, -0.22, 0.36, 0.12, [s]);
    k.box(c.main, -0.5, p.topY(-0.5, s * 0.47) + 0.04, s * 0.47, 0.1, 0.1, 0.07);
    const py = p.topY(-0.95, s * 0.34);
    k.add(new CylinderGeometry(0.045, 0.05, 0.09, 8), c.metal, M(-0.95, py + 0.03, s * 0.34));
    k.add(new CircleGeometry(0.04, 8), c.dark, M(-0.95, py + 0.077, s * 0.34, -PI / 2, 0, 0));
    // Bargeboards between the front wheels and the pods.
    k.box(c.dark, 0.86, 0.26, s * 0.47, 0.38, 0.3, 0.02, 0, s * -0.12, 0);
  }

  // Front wing: raised "spoon" centre, low outer planes, flaps, endplates.
  k.box(c.main, 1.96, 0.225, 0, 0.3, 0.03, 0.6, 0, 0, -0.05);
  for (const s of SIDES) {
    k.box(c.main, 1.94, 0.145, s * 0.6, 0.34, 0.03, 0.56, 0, 0, -0.05);
    k.box(c.dark, 1.82, 0.205, s * 0.6, 0.15, 0.022, 0.56, 0, 0, -0.45);
    k.box(c.main, 1.95, 0.185, s * 0.3, 0.3, 0.1, 0.02);
    k.box(c.main, 1.9, 0.225, s * 0.885, 0.46, 0.22, 0.025);
    k.box(c.main, 1.92, 0.335, s * 0.06, 0.2, 0.22, 0.018);
  }

  // Tall, narrow rear wing with gills in the endplates. The tail narrows into a small crash structure with the rain
  // light on its tip, framed dark (red on the red car would vanish); a thin beam wing between the endplates' feet,
  // and the diffuser: a short dark ramp kicking up between the rear wheels, walled each side.
  k.box(c.main, -1.94, 1.0, 0, 0.36, 0.04, 1.1, 0, 0, -0.14);
  k.box(c.main, -2.08, 1.1, 0, 0.18, 0.03, 1.1, 0, 0, -0.5);
  for (const s of SIDES) {
    k.box(c.main, -1.98, 0.86, s * 0.565, 0.56, 0.62, 0.03);
    k.box(c.accent, -1.98, 1.0, s * 0.582, 0.5, 0.07, 0.006);
    for (let i = 0; i < 3; i++) k.box(c.dark, -1.8 - i * 0.07, 0.72, s * 0.582, 0.02, 0.16, 0.006);
  }
  k.hull(
    new Hull(
      [
        { x: -2.06, w: 0.04, top: 0.41, bot: 0.31 },
        { x: -1.94, w: 0.07, top: 0.44, bot: 0.28 },
        { x: -1.8, w: 0.1, top: 0.46, bot: 0.24 },
      ],
      3,
    ),
    c.main,
  );
  k.box(c.dark, -2.066, 0.36, 0, 0.016, 0.115, 0.1);
  k.box(c.light, -2.072, 0.36, 0, 0.012, 0.085, 0.07);
  k.box(c.dark, -1.9, 0.57, 0, 0.22, 0.025, 1.1, 0, 0, -0.18);
  slat(k, c.dark, -1.42, 0.125, -1.96, 0.3, 0.7);
  for (const s of SIDES) sidePlate(k, c.dark, [[-1.42, 0.12], [-1.96, 0.12], [-1.96, 0.3]], s * 0.35);

  mirrors(k, 0.14, 0.86, 0.46, 0.4);
  wishbones(k, 1.4, 0.12, 0.64, 0.3, 0.44, 0.025);
  wishbones(k, -1.12, 0.18, 0.6, 0.28, 0.5, 0.025);

  return {
    rear: { x: -1.12, r: 0.45, w: 0.48, z: 0.84 },
    front: { x: 1.4, r: 0.34, w: 0.3, z: 0.79 },
    driver: { x: -0.32, y: 0.68, lean: 0.58, wx: 0.04, wy: 0.78, wr: 0.12, rake: 0.6 },
  };
}

/** 2022: ground effect. Long and wide on big covered wheels, a halo, sculpted undercut pods, a multi-element front wing, a slim tail. */
function groundEffect(k: CarKit): BodyLayout {
  const c = k.c;
  const driver: DriverSpec = { x: -0.32, y: 0.68, lean: 0.6, wx: 0.05, wy: 0.78, wr: 0.12, rake: 0.6 };
  const tub = k.hull(
    new Hull(
      [
        { x: -1.95, w: 0.1, top: 0.46, bot: 0.26 },
        { x: -1.6, w: 0.16, top: 0.62, bot: 0.2 },
        { x: -1.15, w: 0.24, top: 0.84, bot: 0.14 },
        { x: -0.78, w: 0.32, top: 0.9, bot: 0.12 },
        { x: -0.3, w: 0.35, top: 0.84, bot: 0.12 },
        { x: 0.25, w: 0.32, top: 0.8, bot: 0.14 },
        { x: 0.8, w: 0.22, top: 0.66, bot: 0.2 },
        { x: 1.4, w: 0.15, top: 0.5, bot: 0.18 },
        { x: 1.95, w: 0.1, top: 0.36, bot: 0.15 },
      ],
      3,
    ),
    c.main,
  );
  k.hull(
    new Hull(
      [
        { x: 1.95, w: 0.1, top: 0.36, bot: 0.15 },
        { x: 2.26, w: 0.07, top: 0.28, bot: 0.13 },
      ],
      3,
    ),
    c.accent,
  );
  cockpit(k, tub, -0.26, 0.34, 0.23);
  k.add(screenGeometry(0.34, 0.06, 0.05, 0.9), c.glass, M(0.1, tub.topY(0.1, 0) - 0.02, 0));
  k.topRoundel(tub, 1.45, 0, 0.1);

  // The halo: a hoop round the driver's head on a single front pillar.
  const hx = driver.x - 0.19 * Math.sin(driver.lean);
  const hy = driver.y + 0.19 * Math.cos(driver.lean) + 0.2;
  const R = 0.45;
  const ARC = 4.5;
  const ry = hy - 0.07;
  k.add(new TorusGeometry(R, 0.036, 6, 22, ARC).applyMatrix4(M(0, 0, 0, 0, 0, -ARC / 2)).applyMatrix4(M(hx, ry, 0, -PI / 2, 0, 0)), c.dark);
  const ex = hx + R * Math.cos(ARC / 2);
  const ez = R * Math.sin(ARC / 2);
  for (const s of SIDES) k.rod(V(ex, ry, s * ez), V(ex - 0.05, tub.topY(ex - 0.05, s * 0.3) - 0.02, s * 0.3), 0.036, c.dark);
  const fx = hx + R + 0.16;
  k.rod(V(hx + R, ry, 0), V(fx, tub.topY(fx, 0) - 0.02, 0), 0.036, c.dark);

  // Airbox behind the halo, tucked under the helmet line, with a triangular mouth.
  const air = k.hull(
    new Hull(
      [
        { x: -1.32, w: 0.08, top: 0.88, bot: 0.66 },
        { x: -1.04, w: 0.13, top: 1.08, bot: 0.74 },
        { x: -0.86, w: 0.15, top: 1.14, bot: 0.8 },
      ],
      3,
    ),
    c.main,
  );
  k.add(new CircleGeometry(1, 3, PI / 2), c.dark, facing(V(-0.855, 0.98, 0), FWD, V(0, 1, 0), 0.11, 0.12));
  k.sideRoundel(air, -1.08, 0.93, 0.09);

  // Sculpted pods with deep undercuts and downwash ramps.
  const pod = new Hull(
    [
      { x: -1.35, w: 0.05, top: 0.34, bot: 0.18, zc: 0.28 },
      { x: -0.95, w: 0.12, top: 0.46, bot: 0.14, zc: 0.36 },
      { x: -0.35, w: 0.21, top: 0.6, bot: 0.16, zc: 0.5 },
      { x: 0.3, w: 0.2, top: 0.64, bot: 0.3, zc: 0.52 },
      { x: 0.5, w: 0.12, top: 0.62, bot: 0.42, zc: 0.52 },
    ],
    4,
  );
  for (const [p, s] of [
    [pod, 1],
    [pod.mirrored(), -1],
  ] as [Hull, Side][]) {
    k.hull(p, c.main);
    k.add(new CircleGeometry(1, 14), c.dark, facing(V(0.505, 0.52, s * 0.52), FWD, V(0, 1, 0), 0.1, 0.07));
    k.sideDecal(p, new ShapeGeometry(stripeShape(p, -0.9, 0.45, 0.05, 0.04), 1), c.accent, [s]);
    k.sideRoundel(p, -0.4, 0.4, 0.12, [s]);
  }

  // The floor, with its edge showing under the pods; its back half tucked in between the rear wheels.
  k.box(c.dark, 0.3, 0.1, 0, 1.6, 0.03, 1.36);
  k.box(c.dark, -1.0, 0.1, 0, 1.0, 0.03, 0.9);

  // Front wing: a full-width main plane under the nose, two flaps, endplates.
  k.box(c.accent, 2.12, 0.125, 0, 0.42, 0.035, 1.9, 0, 0, -0.04);
  for (const s of SIDES) {
    k.box(c.main, 1.99, 0.185, s * 0.56, 0.16, 0.025, 0.82, 0, 0, -0.35);
    k.box(c.accent, 1.96, 0.245, s * 0.6, 0.13, 0.022, 0.74, 0, 0, -0.6);
    k.box(c.accent, 2.12, 0.215, s * 0.96, 0.4, 0.22, 0.025);
  }

  // The tail narrows into a small crash structure with the rain light on its tip. Rear wing with a DRS flap, a thin
  // beam wing, and the diffuser: a short dark ramp kicking up between the rear wheels, walled each side.
  k.hull(
    new Hull(
      [
        { x: -2.2, w: 0.04, top: 0.41, bot: 0.31 },
        { x: -2.08, w: 0.07, top: 0.44, bot: 0.28 },
        { x: -1.95, w: 0.1, top: 0.46, bot: 0.26 },
      ],
      3,
    ),
    c.main,
  );
  k.box(c.light, -2.205, 0.36, 0, 0.02, 0.085, 0.07);
  k.box(c.main, -2.0, 0.98, 0, 0.42, 0.04, 1.3, 0, 0, -0.12);
  k.box(c.accent, -2.16, 1.08, 0, 0.2, 0.03, 1.3, 0, 0, -0.5);
  for (const s of SIDES) k.box(c.accent, -2.04, 0.86, s * 0.665, 0.5, 0.4, 0.03);
  k.box(c.dark, -1.98, 0.53, 0, 0.22, 0.025, 0.84, 0, 0, -0.18);
  slat(k, c.dark, -1.5, 0.115, -2.02, 0.3, 0.74);
  for (const s of SIDES) sidePlate(k, c.dark, [[-1.5, 0.11], [-2.02, 0.11], [-2.02, 0.3]], s * 0.37);

  mirrors(k, 0.18, 0.86, 0.5, 0.42);
  wishbones(k, 1.5, 0.14, 0.64, 0.3, 0.46, 0.025);
  wishbones(k, -1.2, 0.2, 0.62, 0.28, 0.5, 0.025);

  return {
    rear: { x: -1.2, r: 0.46, w: 0.56, z: 0.9 },
    front: { x: 1.5, r: 0.41, w: 0.36, z: 0.82 },
    driver,
  };
}

export const BODIES: Record<BodyKind, (k: CarKit) => BodyLayout> = {
  monoposto,
  streamliner,
  frontEngine,
  rearEngine,
  wedge,
  turbo,
  highNose,
  v10,
  groundEffect,
};
